import React, { useState, useEffect } from 'react';
import { ShoppingCart, Search, ChevronLeft, ChevronRight, TrendingUp, Package, Clock, CheckCircle, XCircle, IndianRupee, Calendar } from 'lucide-react';
import { apiRequest } from '../../../utils/api-request';

interface Order {
  id: string;
  order_number: string;
  created_at: string;
  financial_status: string;
  fulfillment_status: string | null;
  total_price: string;
  currency: string;
  customer: {
    first_name: string;
    last_name: string;
    email: string;
  };
  customer_name?: string;
  email?: string;
  phone?: string;
  latest_delivery_status?: number | null;
  latest_delivery_status_desc?: string | null;
  latest_delivery_location?: string | null;
  latest_delivery_event_at?: string | null;
}

interface RecentOrder {
  id: string;
  order_number: string;
  total_price: string;
  financial_status: string;
  fulfillment_status: string | null;
  order_date: string;
  customer_name: string;
  customer_email: string;
}

interface OrderStats {
  totalOrders: number;
  paidOrders: number;
  pendingOrders: number;
  refundedOrders: number;
  fulfilledOrders: number;
  unfulfilledOrders: number;
  totalRevenue: number;
  averageOrderValue: string;
  ordersThisMonth: number;
  revenueThisMonth: number;
  recentOrders: RecentOrder[];
  topOrders: RecentOrder[];
}

const ITEMS_PER_PAGE = 20;

const getDeliveryStatusBadge = (statusCode: number | null | undefined): { label: string; className: string } => {
  switch (statusCode) {
    case 8: case 48: return { label: 'Delivered', className: 'bg-green-100 text-green-800' };
    case 6:  return { label: 'Out for Delivery', className: 'bg-blue-100 text-blue-800' };
    case 4:  return { label: 'Picked Up', className: 'bg-indigo-100 text-indigo-800' };
    case 9:  return { label: 'Failed Delivery', className: 'bg-red-100 text-red-800' };
    case 14: return { label: 'RTO Delivered', className: 'bg-orange-100 text-orange-800' };
    case 12: case 21: return { label: 'RTO', className: 'bg-orange-100 text-orange-800' };
    case 5:  return { label: 'In Transit', className: 'bg-yellow-100 text-yellow-800' };
    default: return statusCode != null
      ? { label: 'In Transit', className: 'bg-yellow-100 text-yellow-800' }
      : { label: 'No tracking', className: 'bg-gray-100 text-gray-500' };
  }
};

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoadingStats(true);
    try {
      const response = await apiRequest<OrderStats>('/shopify/orders/stats', { method: 'GET' });
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load order stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const searchOrders = async (query: string, page: number = 1, status: string = 'all') => {
    if (!query || query.trim().length < 2) {
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    try {
      const statusParam = status !== 'all' ? `&status=${status}` : '';
      const response = await apiRequest<Order[]>(
        `/shopify/orders/search?q=${encodeURIComponent(query)}&page=${page}&limit=${ITEMS_PER_PAGE}${statusParam}`,
        { method: 'GET' }
      );
      if (response.success && response.data) {
        setOrders(response.data);
        const pagination = (response as any).pagination;
        if (pagination) {
          setTotalOrders(pagination.total);
          setTotalPages(pagination.totalPages);
          setCurrentPage(pagination.page);
        }
      }
    } catch (error) {
      console.error('Failed to search orders:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim().length >= 2) {
      setSearchQuery(searchInput);
      searchOrders(searchInput, 1, statusFilter);
    }
  };

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    if (searchQuery) {
      searchOrders(searchQuery, 1, newStatus);
    }
  };

  const handlePageChange = (page: number) => {
    if (searchQuery) {
      searchOrders(searchQuery, page, statusFilter);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setOrders([]);
    setHasSearched(false);
    setCurrentPage(1);
    setTotalOrders(0);
    setTotalPages(0);
    setStatusFilter('all');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount: number | string, compact: boolean = false) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (compact && num >= 10000000) {
      // Crores (1Cr = 10,000,000)
      return '₹' + (num / 10000000).toFixed(1) + 'Cr';
    } else if (compact && num >= 100000) {
      // Lakhs (1L = 100,000)
      return '₹' + (num / 100000).toFixed(1) + 'L';
    } else if (compact && num >= 1000) {
      // Thousands
      return '₹' + (num / 1000).toFixed(1) + 'K';
    }

    return '₹' + new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(num);
  };

  const getStatusBadge = (status: string, type: 'financial' | 'fulfillment') => {
    if (type === 'financial') {
      switch (status) {
        case 'paid':
          return 'bg-green-100 text-green-800';
        case 'refunded':
          return 'bg-red-100 text-red-800';
        case 'pending':
          return 'bg-yellow-100 text-yellow-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    } else {
      switch (status) {
        case 'fulfilled':
          return 'bg-green-100 text-green-800';
        case 'partial':
          return 'bg-yellow-100 text-yellow-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-brandColor rounded-lg text-white">
              <ShoppingCart className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Orders
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Search and manage your Shopify orders
              </p>
            </div>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex-1 max-w-xl">
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by order #, email, customer name, or phone..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-800 dark:text-white"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-800 dark:text-white text-sm"
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="refunded">Refunded</option>
              </select>
              <button
                type="submit"
                disabled={searchInput.trim().length < 2}
                className="px-4 py-2 bg-brandColor text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Search
              </button>
              {hasSearched && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Stats Section */}
        {isLoadingStats ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
          </div>
        ) : stats && (
          <div className="mb-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.totalOrders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Total Orders</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.paidOrders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Paid</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.pendingOrders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.refundedOrders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Refunded</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <IndianRupee className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(stats.totalRevenue, true)}
                    </p>
                    <p className="text-xs text-gray-500">Total Revenue</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(stats.averageOrderValue, true)}
                    </p>
                    <p className="text-xs text-gray-500">Avg Order Value</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.ordersThisMonth.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">This Month</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Package className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.unfulfilledOrders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Unfulfilled</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent & Top Orders */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Orders */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Recent Orders</h3>
                  </div>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {stats.recentOrders.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 text-center">No orders yet</p>
                  ) : (
                    stats.recentOrders.map((order) => (
                      <div key={order.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">#{order.order_number}</p>
                          <p className="text-xs text-gray-500">{order.customer_name}</p>
                          <p className="text-xs text-gray-400">{formatDate(order.order_date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-brandColor">{formatCurrency(order.total_price)}</p>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(order.financial_status, 'financial')}`}>
                            {order.financial_status}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Top Orders by Value */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-green-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Highest Value Orders</h3>
                  </div>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {stats.topOrders.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 text-center">No orders yet</p>
                  ) : (
                    stats.topOrders.map((order, index) => (
                      <div key={order.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-yellow-100 text-yellow-700' :
                            index === 1 ? 'bg-gray-100 text-gray-700' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {index + 1}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">#{order.order_number}</p>
                            <p className="text-xs text-gray-500">{order.customer_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-green-600">{formatCurrency(order.total_price)}</p>
                          <p className="text-xs text-gray-400">{formatDate(order.order_date)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Results Section */}
        {hasSearched && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Search Results {searchQuery && <span className="text-gray-500 font-normal">for "{searchQuery}"</span>}
              </h2>
              {totalOrders > 0 && (
                <p className="text-sm text-gray-500">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalOrders)} of {totalOrders.toLocaleString()} order(s)
                </p>
              )}
            </div>

            {isSearching ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                <span className="ml-2 text-gray-600">Searching...</span>
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No orders found</h3>
                <p className="text-sm text-gray-500">Try adjusting your search query or filters</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fulfillment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {orders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">#{order.order_number}</div>
                            <div className="text-xs text-gray-500">ID: {order.id}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(order.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {order.customer?.first_name} {order.customer?.last_name}
                            </div>
                            <div className="text-xs text-gray-500">{order.customer?.email || order.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(order.financial_status, 'financial')}`}>
                              {order.financial_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(order.fulfillment_status || 'unfulfilled', 'fulfillment')}`}>
                                {order.fulfillment_status || 'unfulfilled'}
                              </span>
                              {order.latest_delivery_status != null && (
                                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getDeliveryStatusBadge(order.latest_delivery_status).className}`}>
                                  {order.latest_delivery_status_desc || getDeliveryStatusBadge(order.latest_delivery_status).label}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {formatCurrency(order.total_price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Showing <span className="font-medium">{((currentPage - 1) * ITEMS_PER_PAGE) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, totalOrders)}</span> of{' '}
                      <span className="font-medium">{totalOrders.toLocaleString()}</span> results
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span>Previous</span>
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        <span>Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Initial State - No search yet */}
        {!hasSearched && !isLoadingStats && (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Search for Orders</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              With {stats?.totalOrders.toLocaleString() || '0'} orders in your database,
              use the search bar above to find specific orders by order number, email, customer name, or phone.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
